import React from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { RemoveCircle } from "@mui/icons-material";
import { FleetRule, WeekDay, RuleDiscountType } from "../../../../types/fleets.types";

interface Props {
  rule: FleetRule;
  onChange: (r: FleetRule) => void;
  onRemove?: () => void;
  showRemove: boolean;
}

const DAY_OPTIONS: { label: string; value: WeekDay }[] = [
  { label: "Segunda", value: "MONDAY" },
  { label: "Terça", value: "TUESDAY" },
  { label: "Quarta", value: "WEDNESDAY" },
  { label: "Quinta", value: "THURSDAY" },
  { label: "Sexta", value: "FRIDAY" },
  { label: "Sábado", value: "SATURDAY" },
  { label: "Domingo", value: "SUNDAY" },
];

const SettingsRow: React.FC<Props> = ({ rule, onChange, onRemove, showRemove }) => {
  const update = (field: Partial<FleetRule>) => onChange({ ...rule, ...field });

  return (
    <div style={{ display: "flex", gap: "1rem", width: "100%", alignItems: "center", flexWrap: "wrap" }}>
      <FormControl sx={{ minWidth: 140 }}>
        <InputLabel shrink>Dia</InputLabel>
        <Select
          value={rule.weekDay ?? ""}
          label="Dia"
          notched
          displayEmpty
          renderValue={(val) =>
            val ? (DAY_OPTIONS.find((d) => d.value === val)?.label ?? val) : "Todos"
          }
          onChange={(e) => update({ weekDay: (e.target.value as WeekDay) || null })}
        >
          <MenuItem value="">Todos</MenuItem>
          {DAY_OPTIONS.map((d) => (
            <MenuItem key={d.value} value={d.value}>
              {d.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Hora início"
        type="time"
        value={rule.startHour ?? ""}
        onChange={(e) => update({ startHour: e.target.value || null })}
        sx={{ minWidth: 140 }}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <TextField
        label="Hora fim"
        type="time"
        value={rule.endHour ?? ""}
        onChange={(e) => update({ endHour: e.target.value || null })}
        sx={{ minWidth: 140 }}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <FormControl sx={{ minWidth: 160 }}>
        <InputLabel>Tipo de desconto</InputLabel>
        <Select
          value={rule.discountType}
          label="Tipo de desconto"
          onChange={(e) => {
            const type = e.target.value as RuleDiscountType;
            update({ discountType: type, ...(type === "NONE" ? { discount: 0 } : {}) });
          }}
        >
          <MenuItem value="NONE">Sem desconto</MenuItem>
          <MenuItem value="PERCENTAGE">Porcentagem</MenuItem>
          <MenuItem value="AMOUNT">Valor</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Desconto"
        disabled={rule.discountType === "NONE"}
        value={rule.discountType === "NONE" ? "" : rule.discount === 0 ? "" : rule.discount}
        onChange={(e) => {
          const n = parseFloat(e.target.value.replace(",", ".")) || 0;
          update({ discount: n });
        }}
        sx={{ flex: 1, minWidth: 130 }}
        slotProps={{
          input:
            rule.discountType === "PERCENTAGE"
              ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
              : rule.discountType === "AMOUNT"
              ? { startAdornment: <InputAdornment position="start">R$</InputAdornment> }
              : {},
        }}
      />

      {showRemove && (
        <IconButton onClick={onRemove} color="error" size="small">
          <RemoveCircle />
        </IconButton>
      )}
    </div>
  );
};

export default SettingsRow;
